import { useState, useEffect, useRef } from "react";
import { Users, Plus, Edit2, Trash2, Eye, Lock, LogOut, Copy, Check, X, Upload, Camera } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LoginForm({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) throw new Error("Invalid password");
      const data = await res.json();
      onLogin(data.token);
    } catch {
      setError("Invalid password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-yellow-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-slate-900" />
          </div>
          <h1 className="text-2xl font-bold text-white">Admin Access</h1>
          <p className="text-slate-400 text-sm mt-1">Team Card Management</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter admin password"
            className="w-full h-12 px-4 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:border-yellow-500 focus:outline-none"
            data-testid="admin-password-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-bold rounded-lg transition-all disabled:opacity-50"
            data-testid="admin-login-btn"
          >
            {loading ? "Verifying..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
}

function MemberForm({ member, onSave, onCancel, token }) {
  const [form, setForm] = useState({
    name: member?.name || "",
    slug: member?.slug || "",
    designation: member?.designation || "",
    phone: member?.phone || "",
    email: member?.email || "",
    whatsapp: member?.whatsapp || "",
    photo_url: member?.photo_url || "",
    linkedin: member?.linkedin || "",
    department: member?.department || "",
  });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const autoSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  const handleNameChange = (e) => {
    const name = e.target.value;
    setForm(f => ({ ...f, name, slug: member ? f.slug : autoSlug(name) }));
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/upload/photo?token=${token}`, { method: "POST", body: formData });
      if (!res.ok) { const err = await res.json(); alert(err.detail || "Upload failed"); return; }
      const data = await res.json();
      setForm(f => ({ ...f, photo_url: data.url }));
    } catch { alert("Upload failed. Please try again."); }
    finally { setUploading(false); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="member-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Full Name *</label>
          <input required value={form.name} onChange={handleNameChange}
            className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-yellow-500 focus:outline-none text-sm" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">URL Slug *</label>
          <input required value={form.slug} onChange={(e) => setForm(f => ({ ...f, slug: e.target.value }))}
            className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-yellow-500 focus:outline-none text-sm bg-slate-50" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Designation *</label>
          <input required value={form.designation} onChange={(e) => setForm(f => ({ ...f, designation: e.target.value }))}
            className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-yellow-500 focus:outline-none text-sm" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Department</label>
          <input value={form.department} onChange={(e) => setForm(f => ({ ...f, department: e.target.value }))}
            className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-yellow-500 focus:outline-none text-sm" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Phone *</label>
          <input required value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
            className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-yellow-500 focus:outline-none text-sm" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">WhatsApp (if different)</label>
          <input value={form.whatsapp} onChange={(e) => setForm(f => ({ ...f, whatsapp: e.target.value }))}
            placeholder="Leave blank to use phone number"
            className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-yellow-500 focus:outline-none text-sm" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Email</label>
          <input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
            className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-yellow-500 focus:outline-none text-sm" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">LinkedIn URL</label>
          <input value={form.linkedin} onChange={(e) => setForm(f => ({ ...f, linkedin: e.target.value }))}
            placeholder="https://linkedin.com/in/..."
            className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-yellow-500 focus:outline-none text-sm" />
        </div>
        <div className="md:col-span-2">
          <label className="text-sm font-medium text-slate-700 mb-1 block">Profile Photo</label>
          <div className="flex items-center gap-4">
            {/* Preview */}
            <div className="w-16 h-16 rounded-full overflow-hidden bg-slate-100 flex-shrink-0 border-2 border-slate-200">
              {form.photo_url ? (
                <img src={form.photo_url.startsWith("/api") ? `${BACKEND_URL}${form.photo_url}` : form.photo_url} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-slate-900 text-xl font-bold">
                  {form.name?.charAt(0) || "?"}
                </div>
              )}
            </div>
            {/* Upload button */}
            <div className="flex-1">
              <input type="file" ref={fileRef} accept="image/*" onChange={handleUpload} className="hidden" />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2 rounded-lg transition-all text-sm disabled:opacity-50"
                data-testid="upload-photo-btn"
              >
                {uploading ? (
                  <><div className="w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" /> Uploading...</>
                ) : (
                  <><Camera className="w-4 h-4" /> {form.photo_url ? "Change Photo" : "Upload Photo"}</>
                )}
              </button>
              <p className="text-xs text-slate-400 mt-1">JPG, PNG, WebP. Max 5MB</p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <button type="submit" className="bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-semibold px-6 py-2.5 rounded-lg transition-all text-sm" data-testid="save-member-btn">
          {member ? "Update Member" : "Add Member"}
        </button>
        <button type="button" onClick={onCancel} className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-6 py-2.5 rounded-lg transition-all text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function TeamAdminPage() {
  const [token, setToken] = useState(localStorage.getItem("aps_admin_token") || "");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const isLoggedIn = token === "aps-admin-token";

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/team`);
      const data = await res.json();
      setMembers(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn) fetchMembers();
  }, [isLoggedIn]);

  const handleLogin = (t) => {
    setToken(t);
    localStorage.setItem("aps_admin_token", t);
  };

  const handleLogout = () => {
    setToken("");
    localStorage.removeItem("aps_admin_token");
  };

  const handleSave = async (formData) => {
    try {
      const url = editMember
        ? `${API}/team/${editMember.id}?token=${token}`
        : `${API}/team?token=${token}`;
      const method = editMember ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.detail || "Failed to save");
        return;
      }
      setShowForm(false);
      setEditMember(null);
      fetchMembers();
    } catch {
      alert("Failed to save. Please try again.");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name}'s card?`)) return;
    try {
      await fetch(`${API}/team/${id}?token=${token}`, { method: "DELETE" });
      fetchMembers();
    } catch {
      alert("Failed to delete");
    }
  };

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`https://www.amarpreet.in/api/card/${slug}`);
    setCopiedId(slug);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (!isLoggedIn) return <LoginForm onLogin={handleLogin} />;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="team-admin-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-slate-900" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Team Card Manager</h1>
              <p className="text-xs text-slate-500">Amar Preet Scaffolding</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-500 hover:text-red-500 text-sm transition-colors" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Actions */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-slate-600 text-sm">{members.length} team member{members.length !== 1 ? "s" : ""}</p>
          {!showForm && (
            <button
              onClick={() => { setShowForm(true); setEditMember(null); }}
              className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-semibold px-5 py-2.5 rounded-lg transition-all text-sm"
              data-testid="add-member-btn"
            >
              <Plus className="w-4 h-4" /> Add Member
            </button>
          )}
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-900 mb-4">{editMember ? "Edit Member" : "Add New Member"}</h2>
            <MemberForm
              member={editMember}
              onSave={handleSave}
              onCancel={() => { setShowForm(false); setEditMember(null); }}
              token={token}
            />
          </div>
        )}

        {/* Members List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No team members yet. Add your first team member.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {members.map((m) => (
              <div key={m.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4 hover:shadow-md transition-shadow" data-testid={`member-row-${m.slug}`}>
                {/* Photo */}
                <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-100 flex-shrink-0">
                  {m.photo_url ? (
                    <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-slate-900 font-bold text-lg">
                      {m.name?.charAt(0)}
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-900 text-sm">{m.name}</p>
                  <p className="text-yellow-600 text-xs">{m.designation}</p>
                  <p className="text-slate-400 text-xs truncate">{m.phone} {m.email && `| ${m.email}`}</p>
                </div>

                {/* Card Link */}
                <div className="hidden sm:flex items-center gap-1 bg-slate-50 rounded-lg px-3 py-1.5">
                  <span className="text-xs text-slate-500 truncate max-w-[180px]">/team/{m.slug}</span>
                  <button onClick={() => copyLink(m.slug)} className="text-slate-400 hover:text-yellow-600 transition-colors ml-1">
                    {copiedId === m.slug ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <a
                    href={`/team/${m.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                    title="Preview"
                  >
                    <Eye className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => { setEditMember(m); setShowForm(true); }}
                    className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-yellow-600 transition-colors"
                    title="Edit"
                    data-testid={`edit-${m.slug}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(m.id, m.name)}
                    className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                    title="Delete"
                    data-testid={`delete-${m.slug}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
